import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import api from "../../api/api"; // Import Axios instance
import { PaginationInfo } from "../../types/api";

interface EMDContextType {
  emdRecords: any[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchEmdRecords: (page?: number, perPage?: number) => Promise<void>;
  addEmd: (emdDetails: any) => Promise<void>;
  editEmd: (emdDetails: any) => Promise<void>;
  deleteEmd: (id: number | string) => Promise<void>;
}

export const EMDContext = createContext<EMDContextType | undefined>(
  undefined,
);

interface EMDProviderProps {
  children: ReactNode;
}

export const useEmdContext = (): EMDContextType => {
  const context = useContext(EMDContext);
  if (!context) {
    throw new Error("useEmdContext must be used within EMDProvider");
  }
  return context;
};

const EMDProvider: React.FC<EMDProviderProps> = ({ children }) => {
  const [emdRecords, setEmdRecords] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch all EMD records with pagination
  const fetchEmdRecords = useCallback(async (page: number = 1, perPage: number = 15) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/emds?${params.toString()}`);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let emdRecordsData = [];
      let paginationData = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          emdRecordsData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          emdRecordsData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          emdRecordsData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        emdRecordsData = responseData.data;
      } else if (Array.isArray(responseData)) {
        emdRecordsData = responseData;
      }
      
      setEmdRecords(emdRecordsData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch EMD records:", error);
      setEmdRecords([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new EMD record
  const addEmd = async (emdDetails: any): Promise<void> => {
    try {
      const response = await api.post("/emds", emdDetails);
      const newEmd = response.data.data || response.data;
      setEmdRecords((prevRecords) => [...prevRecords, newEmd]);
    } catch (error) {
      console.error("Failed to add EMD record:", error);
      throw error;
    }
  };

  // Edit an existing EMD record
  const editEmd = async (emdDetails: any): Promise<void> => {
    try {
      const response = await api.put(`/emds/${emdDetails.id}`, emdDetails);
      const updatedEmd = response.data.data || response.data;
      setEmdRecords((prevRecords) =>
        prevRecords.map((record) =>
          record.id === emdDetails.id ? updatedEmd : record
        )
      );
    } catch (error) {
      console.error("Failed to edit EMD record:", error);
      throw error;
    }
  };

  // Delete an EMD record
  const deleteEmd = async (id: number | string): Promise<void> => {
    try {
      await api.delete(`/emds/${id}`);
      setEmdRecords((prevRecords) =>
        prevRecords.filter((record) => record.id !== id)
      );
    } catch (error) {
      console.error("Failed to delete EMD record:", error);
      throw error;
    }
  };

  useEffect(() => {
    fetchEmdRecords(1, 15);
  }, [fetchEmdRecords]);

  return (
    <EMDContext.Provider
      value={{
        emdRecords,
        pagination,
        loading,
        fetchEmdRecords,
        addEmd,
        editEmd,
        deleteEmd,
      }}
    >
      {children}
    </EMDContext.Provider>
  );
};

export default EMDProvider;
